import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/money";

const METHODS = ["cash", "check", "bank transfer", "other"];

/** Admin records an offline tuition payment; emails the branded receipt unless unchecked. */
export function MarkPaidDialog({ invoice, onClose, onDone }) {
  const [method, setMethod] = useState("cash");
  const [ref, setRef] = useState("");
  const [sendReceipt, setSendReceipt] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (invoice) { setMethod("cash"); setRef(""); setSendReceipt(true); } }, [invoice]);

  const submit = async () => {
    setBusy(true);
    try {
      const note = [method, ref.trim()].filter(Boolean).join(" · ");
      const { data } = await api.post(`/daycare/tuition/invoices/${invoice.id}/mark-paid`, { note, send_receipt: sendReceipt });
      const r = data.receipt || {};
      toast.success(sendReceipt ? (r.sent ? `Paid · receipt ${r.receipt_number} emailed to ${r.sent} parent${r.sent === 1 ? "" : "s"}` : `Paid · receipt ${r.receipt_number} created (no parent email to send to)`) : `Paid · receipt ${r.receipt_number}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not mark paid"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>{invoice ? `${invoice.student_name} · ${money(invoice.total_cents)} · ${invoice.period_start} → ${invoice.period_end}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-xs font-medium text-slate-600">Payment method</div>
            <div className="flex flex-wrap gap-1.5">
              {METHODS.map((m) => <button key={m} type="button" onClick={() => setMethod(m)} className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${method === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`mark-paid-method-${m.replace(" ", "-")}`}>{m}</button>)}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Reference / check no. (optional)</label>
            <input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={120} className="h-9 w-full rounded-lg border border-slate-200 px-2.5 text-sm outline-none focus:border-primary" data-testid="mark-paid-ref" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} data-testid="mark-paid-send-receipt" />
            Email a PDF receipt to the parents
          </label>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="mark-paid-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60" data-testid="mark-paid-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Mark paid</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
